import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fetchExecutive } from "./api";
import type { Range, ExecutiveData } from "./types";
import { fmtCLP, fmtCLPCompact, fmtDayShort, fmtInt } from "./format";
import { KpiCard } from "./shared/KpiCard";
import { RangeTabs } from "./shared/RangeTabs";
import { LoadingBlock, ErrorBlock, EmptyBlock } from "./shared/StateBlocks";

const RANGE_OPTIONS: { value: Range; label: string }[] = [
  { value: "today", label: "Hoy" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "mtd", label: "Este mes" },
];

export function ExecutiveScreen() {
  const [range, setRange] = useState<Range>("7d");

  const query = useQuery({
    queryKey: ["dashboard", "executive", range],
    queryFn: () => fetchExecutive(range),
    refetchInterval: 5 * 60_000,
  });

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Dashboard · Ejecutivo</p>
          <h1 className="nx-h1">Visión de negocio</h1>
          <p className="text-sm text-fg-muted mt-1">
            KPIs, ventas y tendencia del período. Datos de tickets pagados, vendidos y usados.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RangeTabs options={RANGE_OPTIONS} value={range} onChange={setRange} />
          <button
            onClick={() => query.refetch()}
            className="nx-btn-secondary"
            disabled={query.isFetching}
            aria-label="Actualizar"
          >
            <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} />
          </button>
        </div>
      </header>

      {query.isLoading ? (
        <LoadingBlock label="Cargando dashboard ejecutivo…" />
      ) : query.isError || !query.data ? (
        <ErrorBlock onRetry={() => query.refetch()} />
      ) : (
        <DashboardBody data={query.data} />
      )}
    </div>
  );
}

function DashboardBody({ data }: { data: ExecutiveData }) {
  const k = data.kpis;
  return (
    <>
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard
          label="Ventas brutas"
          value={fmtCLP(k.revenue.current)}
          previousLabel={fmtCLP(k.revenue.previous)}
          deltaPct={k.revenue.deltaPct}
        />
        <KpiCard
          label="Tickets vendidos"
          value={fmtInt(k.ticketsSold.current)}
          previousLabel={fmtInt(k.ticketsSold.previous)}
          deltaPct={k.ticketsSold.deltaPct}
        />
        <KpiCard
          label="Validados en puerta"
          value={fmtInt(k.validated.current)}
          previousLabel={fmtInt(k.validated.previous)}
          deltaPct={k.validated.deltaPct}
        />
        <KpiCard
          label="Ticket promedio"
          value={fmtCLP(k.avgTicketPrice.current)}
          previousLabel={fmtCLP(k.avgTicketPrice.previous)}
          deltaPct={k.avgTicketPrice.deltaPct}
        />
        <KpiCard
          label="Nuevos usuarios"
          value={fmtInt(k.newUsers.current)}
          previousLabel={fmtInt(k.newUsers.previous)}
          deltaPct={k.newUsers.deltaPct}
        />
      </section>

      <section className="nx-card p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="nx-h2">Tendencia de ventas</h2>
          <span className="text-2xs text-fg-subtle">
            {rangeLabel(data.range)} · ingresos y tickets
          </span>
        </div>
        <SeriesChart data={data} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="nx-card p-4">
          <h2 className="nx-h2 mb-3">Próximos eventos</h2>
          {data.upcoming.length === 0 ? (
            <EmptyBlock label="No hay eventos próximos." />
          ) : (
            <div className="flex flex-col divide-y divide-border-subtle">
              {data.upcoming.map((ev) => (
                <UpcomingRow key={ev.id} ev={ev} />
              ))}
            </div>
          )}
        </div>

        <div className="nx-card p-4">
          <h2 className="nx-h2 mb-3">Top productores</h2>
          {data.topProducers.length === 0 ? (
            <EmptyBlock label="Sin ventas en el período." />
          ) : (
            <TopProducersList data={data.topProducers} />
          )}
        </div>
      </section>
    </>
  );
}

function SeriesChart({ data }: { data: ExecutiveData }) {
  const series = data.series.map((s) => ({
    day: fmtDayShort(s.day),
    revenue: s.revenue,
    qty: s.qty,
  }));

  return (
    <div className="w-full h-72">
      <ResponsiveContainer>
        <AreaChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--nx-accent)" stopOpacity={0.4} />
              <stop offset="100%" stopColor="var(--nx-accent)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="var(--nx-border-subtle)" vertical={false} />
          <XAxis
            dataKey="day"
            tick={{ fill: "var(--nx-fg-subtle)", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: "var(--nx-fg-subtle)", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => fmtCLPCompact(v)}
            width={60}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fill: "var(--nx-fg-subtle)", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={30}
          />
          <Tooltip content={<SeriesTooltip />} />
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="revenue"
            name="Ingresos"
            stroke="var(--nx-accent)"
            strokeWidth={2}
            fill="url(#revFill)"
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="qty"
            name="Tickets"
            stroke="var(--nx-success)"
            strokeWidth={2}
            strokeDasharray="4 4"
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function SeriesTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const rev = payload.find((p: any) => p.dataKey === "revenue")?.value ?? 0;
  const qty = payload.find((p: any) => p.dataKey === "qty")?.value ?? 0;
  return (
    <div className="nx-card p-2.5 text-xs shadow-lg min-w-[140px]">
      <div className="text-fg-subtle mb-1">{label}</div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-fg-muted">Ingresos</span>
        <span className="text-fg font-semibold tabular-nums">{fmtCLP(rev)}</span>
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-fg-muted">Tickets</span>
        <span className="text-fg font-semibold tabular-nums">{fmtInt(qty)}</span>
      </div>
    </div>
  );
}

function UpcomingRow({ ev }: { ev: ExecutiveData["upcoming"][number] }) {
  const fechaTxt = ev.fecha ? fmtDayShort(ev.fecha) : "—";
  const fillRate = ev.fillRate;
  const barColor =
    fillRate === null
      ? "bg-bg-subtle"
      : fillRate >= 80
        ? "bg-success"
        : fillRate >= 40
          ? "bg-warning"
          : "bg-danger";
  return (
    <div className="py-2.5 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-fg truncate">
          {ev.nombre || "Evento sin nombre"}
        </div>
        <div className="text-2xs text-fg-subtle">
          {ev.club || "Sin club"} · {fechaTxt}
        </div>
      </div>
      <div className="text-right">
        <div className="text-sm font-semibold text-fg tabular-nums">
          {fmtInt(ev.ticketsSold)}
          {ev.aforo ? <span className="text-fg-subtle font-normal">/{fmtInt(ev.aforo)}</span> : null}
        </div>
        <div className="text-2xs text-fg-subtle tabular-nums">{fmtCLP(ev.revenue)}</div>
        {fillRate !== null && (
          <div className="mt-1 w-24 h-1 bg-bg-subtle rounded overflow-hidden">
            <div
              className={`h-full ${barColor}`}
              style={{ width: `${Math.min(100, fillRate)}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function TopProducersList({ data }: { data: ExecutiveData["topProducers"] }) {
  const maxRev = Math.max(...data.map((p) => p.revenue), 1);
  return (
    <div className="flex flex-col divide-y divide-border-subtle">
      {data.map((p, i) => (
        <div key={p.id} className="py-2.5 flex items-center gap-3">
          <div className="text-sm font-bold text-accent w-6 tabular-nums">#{i + 1}</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-fg truncate">{p.nombre}</div>
            <div className="text-2xs text-fg-subtle">{fmtInt(p.qty)} tickets</div>
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold text-fg tabular-nums">{fmtCLP(p.revenue)}</div>
            <div className="mt-1 w-24 h-1 bg-bg-subtle rounded overflow-hidden">
              <div
                className="h-full bg-accent"
                style={{ width: `${Math.round((p.revenue / maxRev) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function rangeLabel(range: Range): string {
  return (
    { today: "hoy", "7d": "últimos 7 días", "30d": "últimos 30 días", mtd: "este mes" }[range] ||
    range
  );
}
